import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, Upload, Trash2 } from "lucide-react";
import { api, API } from "../../lib/api";

const inputCls = "w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-100 outline-none focus:border-[#0A8B85] disabled:opacity-60";
const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-neutral-400";

const emptyThresholds = { min_crs_score: 60, min_dscr: 1.35, required_docs: [], custom_docs: [], disqualifiers: [], custom_disqualifiers: [], interest_rate_pct: 16, max_tenor_months: 36, max_facility_kes: 20000000 };

const toForm = (t) => ({
  name: t?.name || "",
  subdomain: t?.subdomain || "",
  brand_primary_color: t?.brand_primary_color || "#0A8B85",
  contact_name: t?.contact_name || "",
  contact_email: t?.contact_email || "",
  notes: t?.notes || "",
  status: t?.status || "active",
  partner_type: t?.partner_type || "lender",
  focus_sectors_text: (t?.focus_sectors || []).join(", "),
  sector_gate_question: t?.sector_gate_question || "",
  landing_headline: t?.landing_headline || "",
  landing_subline: t?.landing_subline || "",
  landing_eyebrow: t?.landing_eyebrow || "",
  landing_cta: t?.landing_cta || "",
  website_url: t?.website_url || "",
  thresholds: { ...emptyThresholds, ...(t?.thresholds || {}) },
});

function Field({ label, children, hint, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className={labelCls}>{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[10px] text-neutral-500">{hint}</span>}
    </label>
  );
}

function CustomList({ items, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (!v) return; onChange([...items, v]); setDraft(""); };
  return (
    <div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} className={inputCls}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`${testid}-input`} />
        <button type="button" onClick={add} className="rounded-lg border border-neutral-700 px-2.5 text-neutral-300 hover:bg-neutral-800" data-testid={`${testid}-add`}><Plus size={14} /></button>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <li key={i} className="inline-flex items-center gap-1 rounded-full bg-neutral-800 px-2.5 py-1 text-[11px] text-neutral-200" data-testid={`${testid}-item-${i}`}>
              {it}<button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-red-300"><X size={11} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LogoPicker({ tenant, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/tenants/${tenant.tenant_id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged(data); toast.success("Logo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Logo upload failed"); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/admin/tenants/${tenant.tenant_id}/logo`); onChanged(data); toast.success("Logo removed"); }
    catch { toast.error("Couldn't remove logo"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3" data-testid="tenant-logo-picker">
      <div className="flex h-14 w-24 items-center justify-center overflow-hidden rounded-lg border border-neutral-700 bg-white/95">
        {tenant.logo_url ? <img src={`${API.replace(/\/api$/, "")}${tenant.logo_url}`} alt="logo" className="max-h-12 max-w-[88px] object-contain" data-testid="tenant-logo-img" />
          : <span className="text-[10px] text-neutral-400">No logo</span>}
      </div>
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-neutral-700 px-3 py-2 text-xs font-semibold text-neutral-200 hover:bg-neutral-800">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload logo
        <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="tenant-logo-input" />
      </label>
      {tenant.logo_url && <button type="button" onClick={remove} disabled={busy} className="text-neutral-500 hover:text-red-300" data-testid="tenant-logo-remove"><Trash2 size={14} /></button>}
      <span className="text-[10px] text-neutral-500">PNG, JPG, SVG or WebP · max 2 MB</span>
    </div>
  );
}

export const TenantForm = ({ tenant, presets, onSaved, onCancel, onLogoChanged }) => {
  const [f, setF] = useState(() => toForm(tenant));
  const [busy, setBusy] = useState(false);
  const th = f.thresholds;
  const setTh = (patch) => setF({ ...f, thresholds: { ...th, ...patch } });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const toggleDoc = (key) => setTh({ required_docs: th.required_docs.includes(key) ? th.required_docs.filter((k) => k !== key) : [...th.required_docs, key] });
  const dqOn = (key) => th.disqualifiers.some((d) => d.key === key);
  const dqVal = (key) => th.disqualifiers.find((d) => d.key === key)?.value ?? "";
  const toggleDq = (p) => setTh({ disqualifiers: dqOn(p.key) ? th.disqualifiers.filter((d) => d.key !== p.key) : [...th.disqualifiers, { key: p.key, value: p.default != null ? String(p.default) : null }] });
  const setDqVal = (key, value) => setTh({ disqualifiers: th.disqualifiers.map((d) => (d.key === key ? { ...d, value } : d)) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { focus_sectors_text, ...rest } = f;
    const body = { ...rest, focus_sectors: focus_sectors_text.split(",").map((s) => s.trim()).filter(Boolean),
      thresholds: { ...th, min_crs_score: Number(th.min_crs_score), min_dscr: Number(th.min_dscr), interest_rate_pct: Number(th.interest_rate_pct), max_tenor_months: Number(th.max_tenor_months), max_facility_kes: Number(th.max_facility_kes) } };
    try {
      const { data } = tenant ? await api.put(`/admin/tenants/${tenant.tenant_id}`, body) : await api.post("/admin/tenants", body);
      toast.success(tenant ? "Tenant updated" : "Tenant created — you can now upload a logo");
      onSaved(data, !tenant);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg?.replace(/^Value error, /, "")).join("; ") : d || "Couldn't save tenant");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="tenant-form">
      <section className="grid gap-4 sm:grid-cols-2">
        <Field label="Tenant name *"><input required value={f.name} onChange={set("name")} placeholder="e.g. Sidian Bank" className={inputCls} data-testid="tenant-name" /></Field>
        <Field label="Subdomain *" hint="Lowercase letters, numbers and hyphens">
          <div className="flex items-center rounded-lg border border-neutral-700 bg-neutral-900 focus-within:border-[#0A8B85]">
            <input required value={f.subdomain} onChange={(e) => setF({ ...f, subdomain: e.target.value.toLowerCase() })} placeholder="sidian"
              className="w-full bg-transparent px-3 py-2 text-xs text-neutral-100 outline-none" data-testid="tenant-subdomain" />
            <span className="whitespace-nowrap pr-3 text-xs text-neutral-500">.{presets.base_domain}</span>
          </div>
        </Field>
        <Field label="Brand primary colour *">
          <div className="flex items-center gap-2">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(f.brand_primary_color) ? f.brand_primary_color : "#0A8B85"} onChange={set("brand_primary_color")} className="h-9 w-12 cursor-pointer rounded border border-neutral-700 bg-neutral-900" data-testid="tenant-colour-swatch" />
            <input value={f.brand_primary_color} onChange={set("brand_primary_color")} placeholder="#1F4E79" className={inputCls} data-testid="tenant-colour" />
          </div>
        </Field>
        <Field label="Status">
          <select value={f.status} onChange={set("status")} className={inputCls} data-testid="tenant-status">
            <option value="active">Active</option><option value="inactive">Inactive (subdomain offline)</option>
          </select>
        </Field>
        <Field label="Contact person"><input value={f.contact_name} onChange={set("contact_name")} placeholder="e.g. Jane Wanjiru, Head of SME Banking" className={inputCls} data-testid="tenant-contact-name" /></Field>
        <Field label="Contact email"><input type="email" value={f.contact_email} onChange={set("contact_email")} placeholder="name@bank.co.ke" className={inputCls} data-testid="tenant-contact-email" /></Field>
      </section>

      <section className="rounded-xl border border-neutral-800 bg-neutral-950/60 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Partner type & landing page</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field label="Partner type" hint="Lenders get the loan-qualification landing; ecosystem partners get a sector check before sign-up">
            <select value={f.partner_type} onChange={set("partner_type")} className={inputCls} data-testid="tenant-partner-type">
              <option value="lender">Lender (bank / SACCO / credit)</option>
              <option value="ecosystem">Ecosystem partner (DFI / accelerator / programme)</option>
            </select>
          </Field>
          <Field label="Partner website" hint="Shown as “Learn more about what we do at …” on the landing page"><input type="url" value={f.website_url} onChange={set("website_url")} placeholder="https://fsdafrica.org/" className={inputCls} data-testid="tenant-website-url" /></Field>
          <Field label="Landing headline" hint="Leave blank for the default"><input value={f.landing_headline} onChange={set("landing_headline")} placeholder="e.g. Connecting climate, nature and energy businesses to funders and accelerators." className={inputCls} data-testid="tenant-landing-headline" /></Field>
          {f.partner_type === "ecosystem" && (
            <>
              <Field label="Focus sectors" hint="Comma-separated, e.g. climate, nature, energy"><input value={f.focus_sectors_text} onChange={set("focus_sectors_text")} placeholder="climate, nature, energy" className={inputCls} data-testid="tenant-focus-sectors" /></Field>
              <Field label="Sector check question" hint="Asked with Yes / No before sign-up · blank = auto from focus sectors"><input value={f.sector_gate_question} onChange={set("sector_gate_question")} placeholder="Is your business operating in climate, nature, or energy?" className={inputCls} data-testid="tenant-sector-gate-question" /></Field>
            </>
          )}
          <div className="sm:col-span-2"><Field label="Landing sub-line" hint="Leave blank for the default"><textarea rows={2} value={f.landing_subline} onChange={set("landing_subline")} className={inputCls} data-testid="tenant-landing-subline" /></Field></div>
          <Field label="Landing eyebrow" hint="Small caps line above the headline · blank = “[Name] · SME Lending”"><input value={f.landing_eyebrow} onChange={set("landing_eyebrow")} placeholder="e.g. Lasiri Capital · LPO Financing" className={inputCls} data-testid="tenant-landing-eyebrow" /></Field>
          <Field label="Call-to-action button" hint="Blank = “Start my assessment”"><input value={f.landing_cta} onChange={set("landing_cta")} placeholder="e.g. Check My Eligibility" className={inputCls} data-testid="tenant-landing-cta" /></Field>
        </div>
      </section>

      {tenant && <section><span className={labelCls}>Logo</span><LogoPicker tenant={tenant} onChanged={onLogoChanged} /></section>}
      {!tenant && <p className="text-[11px] text-neutral-500">Logo upload becomes available after the tenant is created.</p>}

      <section className="rounded-xl border border-neutral-800 bg-neutral-950/60 p-4">
        <h3 className="text-sm font-bold text-white">Qualification thresholds</h3>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field label="Minimum Capital Readiness Score" hint="0 – 100">
            <input type="number" min={0} max={100} value={th.min_crs_score} onChange={(e) => setTh({ min_crs_score: e.target.value })} className={inputCls} data-testid="tenant-min-crs" />
          </Field>
          <Field label="Minimum DSCR" hint="Debt service coverage ratio · default 1.35x">
            <input type="number" min={0} max={10} step={0.05} value={th.min_dscr} onChange={(e) => setTh({ min_dscr: e.target.value })} className={inputCls} data-testid="tenant-min-dscr" />
          </Field>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Field label="Indicative interest rate" hint="% per year, for facility sizing">
            <input type="number" min={0} max={100} step={0.5} value={th.interest_rate_pct} onChange={(e) => setTh({ interest_rate_pct: e.target.value })} className={inputCls} data-testid="tenant-interest-rate" />
          </Field>
          <Field label="Max tenor" hint="months">
            <input type="number" min={1} max={240} value={th.max_tenor_months} onChange={(e) => setTh({ max_tenor_months: e.target.value })} className={inputCls} data-testid="tenant-max-tenor" />
          </Field>
          <Field label="Max facility" hint="KES · 0 = no cap">
            <input type="number" min={0} step={100000} value={th.max_facility_kes} onChange={(e) => setTh({ max_facility_kes: e.target.value })} className={inputCls} data-testid="tenant-max-facility" />
          </Field>
        </div>

        <div className="mt-5">
          <span className={labelCls}>Required documents</span>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {presets.docs.map((d) => (
              <label key={d.key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-neutral-200 hover:bg-neutral-900">
                <input type="checkbox" checked={th.required_docs.includes(d.key)} onChange={() => toggleDoc(d.key)} className="accent-[#0A8B85]" data-testid={`tenant-doc-${d.key}`} />{d.label}
              </label>
            ))}
          </div>
          <div className="mt-2"><CustomList items={th.custom_docs} onChange={(v) => setTh({ custom_docs: v })} placeholder="Add a custom document requirement…" testid="tenant-custom-doc" /></div>
        </div>

        <div className="mt-5">
          <span className={labelCls}>Hard-check disqualifiers</span>
          <div className="space-y-1.5">
            {presets.disqualifiers.map((p) => (
              <div key={p.key} className="flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-neutral-200 hover:bg-neutral-900">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={dqOn(p.key)} onChange={() => toggleDq(p)} className="accent-[#0A8B85]" data-testid={`tenant-dq-${p.key}`} />{p.label}
                </label>
                {p.param && dqOn(p.key) && (
                  <input value={dqVal(p.key)} onChange={(e) => setDqVal(p.key, e.target.value)} placeholder={p.unit}
                    className="w-40 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-100 outline-none focus:border-[#0A8B85]" data-testid={`tenant-dq-${p.key}-value`} />
                )}
                {p.param && dqOn(p.key) && <span className="text-[10px] text-neutral-500">{p.unit}</span>}
              </div>
            ))}
          </div>
          <div className="mt-2"><CustomList items={th.custom_disqualifiers} onChange={(v) => setTh({ custom_disqualifiers: v })} placeholder="Add a custom disqualifying rule…" testid="tenant-custom-dq" /></div>
        </div>
      </section>

      <Field label="Internal notes"><textarea rows={3} value={f.notes} onChange={set("notes")} placeholder="Anything the team should know about this tenant" className={inputCls} data-testid="tenant-notes" /></Field>

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-2 text-xs font-semibold text-neutral-400 hover:text-white" data-testid="tenant-form-cancel">Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white hover:bg-[#087a74] disabled:opacity-60" data-testid="tenant-form-submit">
          {busy && <Loader2 size={13} className="animate-spin" />}{tenant ? "Save changes" : "Create tenant"}
        </button>
      </div>
    </form>
  );
};
